import PropTypes from 'prop-types';
import { Link } from 'react-router';

import { fetchMtrDetail } from '#/api/imsMgmt/settings/mtr/fetchDetail';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import SideMsg from '#/modules/commonComponents/SideMsg';
import { useCan } from '#/modules/infra/auth/useCan';
import {
  mtrSideMsgDetailBlock,
  mtrSideMsgStatusBlock,
} from '#/modules/imsMgmt/settings/mtr/data/mtrSideMsgConfig';
import {
  canActivateMtr,
  canDeactivateMtr,
} from '#/modules/imsMgmt/settings/mtr/helper/mtrFilterOptions';
import useMtrStatusMutation, {
  MTR_ACTIVATE_CONFIRM,
  MTR_DEACTIVATE_CONFIRM,
} from '#/modules/imsMgmt/settings/mtr/hooks/useMtrStatusMutation';

const MtrSideMsg = ({ displayData, setSelectedData }) => {
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const canActivatePerm = useCan(PERM.MTR_ACTIVATE);

  const id = displayData?.id;
  const status = displayData?.status;
  const updatedAt = displayData?.record?.updatedAt ?? displayData?.updatedAt;
  const canActivate = canActivatePerm && canActivateMtr(status);
  const canDeactivate = canActivatePerm && canDeactivateMtr(status);
  const canOpenDetail = Boolean(id);

  async function refreshSelected() {
    if (!id) {
      return;
    }
    try {
      const detail = await fetchMtrDetail(id);
      setSelectedData(detail?.data ?? null);
    } catch {
      setSelectedData(null);
    }
  }

  const statusMutation = useMtrStatusMutation({
    id,
    getUpdatedAt: () => updatedAt,
    onAfterSuccess: async () => {
      await refreshSelected();
    },
    onVersionConflict: () => {
      refreshSelected();
    },
  });

  const isPending = statusMutation.isPending;
  const hasActions = canOpenDetail || canActivate || canDeactivate;

  async function handleActivate() {
    const ok = await confirm(MTR_ACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'activate' });
  }

  async function handleDeactivate() {
    const ok = await confirm(MTR_DEACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'deactivate' });
  }

  const blocks = [
    mtrSideMsgStatusBlock,
    {
      condition: () => hasActions,
      fields: [
        {
          key: 'actions',
          label: '操作',
          render: () => (
            <div className="side_msg-row">
              <h3>操作</h3>
              <div className="flex flex-wrap items-center gap-2">
                {canOpenDetail ? (
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.mtrDetail(id)}>詳情</Link>
                  </Button>
                ) : null}
                {canActivate ? (
                  <Button type="button" disabled={isPending} onClick={handleActivate}>
                    啟用
                  </Button>
                ) : null}
                {canDeactivate ? (
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={isPending}
                    onClick={handleDeactivate}
                  >
                    停用
                  </Button>
                ) : null}
              </div>
            </div>
          ),
        },
      ],
    },
    mtrSideMsgDetailBlock,
  ];

  return (
    <>
      {ConfirmDialogHost}
      <SideMsg
        title="物料"
        titleValue="name"
        displayData={displayData}
        blocks={blocks}
      />
    </>
  );
};

MtrSideMsg.propTypes = {
  displayData: PropTypes.object,
  setSelectedData: PropTypes.func.isRequired,
};

export default MtrSideMsg;
